"use client";

import type { Media } from "@mw/shared/payload-types";

import { NativeAudioProvider, useNativeAudio } from "@/app/_providers/native-audio/NativeAudioProvider";
import { Button } from "@ui/components/core/Button";
import { PlayPauseButton } from "@ui/components/core/PlayPauseButton";
import CoverArt from "@ui/components/routes/music/CoverArt";
import { MusicColorScheme } from "@ui/components/routes/music/colorScheme";
import { getAudioUrl } from "@utilities/payload";
import clsx from "clsx";
import { SkipBack, SkipForward } from "lucide-react";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";

const COLOR_SCHEMES: MusicColorScheme[] = [
  MusicColorScheme.red,
  MusicColorScheme.purple,
  MusicColorScheme.blue,
  MusicColorScheme.cyan,
  MusicColorScheme.green,
  MusicColorScheme.orange,
  MusicColorScheme.black,
];

export interface MusicPlayerItem {
  title: string;
  styles: string[];
  audio: Media | string;
  colorScheme?: MusicColorScheme;
}

export interface MusicPlayerProps {
  items: MusicPlayerItem[];
  trackingSource?: string;
}

function getTrackingProps(activeIndex: number, trackingSource?: string, songTitle?: string) {
  return {
    source: trackingSource,
    song_index: activeIndex,
    song_title: songTitle ?? "",
  };
}

export function MusicPlayerContent({ items, trackingSource }: MusicPlayerProps) {
  const { audioRef, togglePlay, play, playingId, isPaused } = useNativeAudio();
  const [activeIndex, setActiveIndex] = useState(0);
  const [audioEl, setAudioEl] = useState<HTMLAudioElement | null>(null);

  // Sync ref to state so CoverArt gets the element for the orb (ref.current doesn't trigger re-renders)
  useLayoutEffect(() => {
    if (audioRef.current && audioRef.current !== audioEl) {
      setAudioEl(audioRef.current);
    }
  }, [audioRef, audioEl]);

  const songs = useMemo(() => {
    return items.map((item, index) => ({
      ...item,
      audioUrl: typeof item.audio === "string" ? item.audio : getAudioUrl(item.audio) || "",
      colorScheme: item.colorScheme ?? COLOR_SCHEMES[index % COLOR_SCHEMES.length],
      id: `music-player-${index}`,
    }));
  }, [items]);

  const currentSong = songs[activeIndex];
  const isPlaying = playingId === currentSong?.id && !isPaused;

  const navigate = useCallback(
    (direction: "next" | "prev") => {
      const delta = direction === "next" ? 1 : -1;
      const newIndex = Math.max(0, Math.min(activeIndex + delta, songs.length - 1));
      if (newIndex === activeIndex) return;
      setActiveIndex(newIndex);

      const newSong = songs[newIndex];
      if (!newSong?.audioUrl) return;

      const wasPlaying = playingId === currentSong?.id && !isPaused;
      if (wasPlaying) {
        play({
          id: newSong.id,
          url: newSong.audioUrl,
          trackingProps: getTrackingProps(newIndex, trackingSource, newSong.title),
        });
      }
    },
    [activeIndex, songs, currentSong?.id, playingId, isPaused, trackingSource],
  );

  const handleTogglePlay = useCallback(() => {
    if (!currentSong?.audioUrl) return;
    togglePlay({
      id: currentSong.id,
      url: currentSong.audioUrl,
      trackingProps: getTrackingProps(activeIndex, trackingSource, currentSong.title),
    });
  }, [currentSong, activeIndex, trackingSource, togglePlay]);

  if (songs.length === 0) {
    return null;
  }

  return (
    <div className='tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center'>
      <CoverArt
        audioElement={audioEl}
        colorScheme={currentSong.colorScheme}
        hidePlayButton
        onClick={handleTogglePlay}
        songId={currentSong.id}
        styles={currentSong.styles}
        title={currentSong.title}
        boxShadow
        className='tw-w-full tw-max-w-72 tw-max-h-72 tw-flex-1 tw-flex tw-items-center'
      />

      <div className='tw-flex tw-w-full tw-max-w-72 tw-items-center tw-justify-between tw-pt-3'>
        {songs.length <= 1 ? (
          <div className='tw-w-9 sm:tw-w-11' />
        ) : (
          <NavButton
            disabled={activeIndex === 0}
            onClick={() => navigate("prev")}
            type='prev'
            className={activeIndex === 0 ? "tw-opacity-80" : undefined}
          />
        )}
        <PlayPauseButton
          isPlaying={isPlaying}
          onClick={handleTogglePlay}
          iconSize='sm'
          className='tw-h-9 tw-w-9 tw-rounded-3xl sm:tw-h-11 sm:tw-w-11 tw-bg-white tw-text-black'
          style={{ boxShadow: "0 0 1px rgb(0 0 0 / 0.4), 0 4px 4px rgb(0 0 0 / 0.04)" }}
        />
        {songs.length <= 1 ? (
          <div className='tw-w-9 sm:tw-w-11' />
        ) : (
          <NavButton
            disabled={activeIndex === songs.length - 1}
            onClick={() => navigate("next")}
            type='next'
            className={activeIndex === songs.length - 1 ? "tw-opacity-80" : undefined}
          />
        )}
      </div>
    </div>
  );
}

function NavButton({
  className,
  disabled,
  onClick,
  type,
}: {
  className?: string;
  disabled?: boolean;
  onClick: () => void;
  type: "next" | "prev";
}) {
  return (
    <Button
      className={clsx("tw-text-black/60 hover:tw-text-black", disabled && "tw-pointer-events-none", className)}
      disabled={disabled}
      onClick={onClick}
      size='pill'
      variant='ghost'
    >
      {type === "prev" ? <SkipBack size={16} fill='currentColor' /> : <SkipForward size={16} fill='currentColor' />}
    </Button>
  );
}

export function MusicPlayer({ items, trackingSource }: MusicPlayerProps) {
  return (
    <NativeAudioProvider>
      <MusicPlayerContent items={items} trackingSource={trackingSource} />
    </NativeAudioProvider>
  );
}

export default MusicPlayer;
